import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { deleteAvatar } from '../../api/ivrAgents.js';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * DeleteAvatarDialog — 刪除確認 modal
 *   - 必須輸入 avatar 名稱（完全相同）才解鎖刪除按鈕
 *   - 點刪除 → soft delete + onDeleted callback
 */
export function DeleteAvatarDialog({ open, avatar, onClose, onDeleted }) {
    const { t } = useTranslation('ivrAgents');
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setInput('');
            setError(null);
            setBusy(false);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open, avatar?.avatarId]);

    if (!open || !avatar) return null;

    const canDelete = input.trim() === avatar.name && !busy;

    const handleDelete = async () => {
        if (!canDelete) return;
        setBusy(true);
        setError(null);
        try {
            await deleteAvatar(avatar.avatarId);
            onDeleted?.(avatar.avatarId);
            // 通知 quota pill 即時減一
            window.dispatchEvent(new CustomEvent('qubby:avatar-changed', { detail: { reason: 'delete', avatarId: avatar.avatarId } }));
            onClose();
        } catch (e) {
            setError(e);
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={18} strokeWidth={1.8} className="text-danger-500" />
                        <h2 className="heading-display text-lg font-semibold text-ink-900">{t('delete.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="text-sm text-ink-700 leading-relaxed">
                        {t('delete.aboutToDelete')} <span className="font-semibold text-ink-900">「{avatar.name}」</span>。
                        {t('delete.afterDelete')}
                    </div>
                    <div className="rounded-md bg-danger-100/40 border border-danger-500/30 px-3 py-2 text-xs text-danger-500">
                        {t('delete.warning')}
                    </div>

                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                            {t('delete.confirmLabel')}
                        </label>
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={busy}
                            placeholder={avatar.name}
                            className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-danger-500 outline-none rounded-md bg-white font-mono disabled:bg-cream-100"
                            autoComplete="off"
                        />
                        {input && input !== avatar.name && (
                            <div className="text-[11px] text-ink-400 mt-1">{t('delete.nameMismatch')}</div>
                        )}
                    </div>

                    {error && (
                        <div className="text-sm text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded-md px-3 py-2">
                            {t('delete.failed', { message: error.message })}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={!canDelete}
                        className="px-4 py-2 text-sm rounded-md bg-danger-500 text-white hover:bg-danger-500/90 transition disabled:bg-cream-300 disabled:text-ink-400 disabled:cursor-not-allowed"
                    >
                        {busy ? t('delete.deleting') : t('delete.permanentRemove')}
                    </button>
                </div>
            </div>
        </div>
    );
}
